/**
 * Site device password helpers (Site > Configure > Device credentials) -- pure, dependency-free so
 * `npm run test:unit` exercises them with Node's runner.
 *
 * The policy MIRRORS services/site_device_credentials.validate_admin_password on the controller,
 * which stays the authority (it re-checks every PUT). Mirroring it here lets the Save button explain
 * itself live instead of bouncing a 400. Characters ? " ' \ ! # and spaces are excluded because
 * ecCLI treats them specially inside `username <u> password 0 <password>` (context help, quoting,
 * comments); 32 is the Edgecore local-account limit (UNVERIFIED on this hardware, same as server).
 */

export const PASSWORD_MIN = 12;
export const PASSWORD_MAX = 32;
export const FORBIDDEN_CHARS = "?\"'\\!#";
/** Same alphabet the controller generates from: switch-safe, no look-alike quoting issues. */
export const GENERATE_ALPHABET =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_.@%+=:,";
/** How long a revealed password stays on screen (the server also advises 30 s). */
export const REVEAL_SECONDS = 30;

export interface StrengthCheck {
  id: "length" | "charset" | "classes" | "not_username" | "changed";
  label: string;
  ok: boolean;
}

/** Printable ASCII (0x21-0x7E) minus the forbidden set -- no whitespace at all. */
function allowedChar(ch: string): boolean {
  const code = ch.charCodeAt(0);
  return code >= 0x21 && code <= 0x7e && !FORBIDDEN_CHARS.includes(ch);
}

/** Count of character classes present: lowercase, uppercase, digit, symbol. */
export function characterClasses(pw: string): number {
  let lower = false;
  let upper = false;
  let digit = false;
  let symbol = false;
  for (const ch of pw) {
    if (ch >= "a" && ch <= "z") lower = true;
    else if (ch >= "A" && ch <= "Z") upper = true;
    else if (ch >= "0" && ch <= "9") digit = true;
    else symbol = true;
  }
  return [lower, upper, digit, symbol].filter(Boolean).length;
}

/**
 * Live strength checklist for a candidate admin password. `changed` is only meaningful after a
 * reveal (the UI knows the current value then); otherwise the server enforces it on save.
 */
export function checkPassword(
  pw: string,
  username: string,
  current?: string | null,
): { ok: boolean; checks: StrengthCheck[] } {
  const checks: StrengthCheck[] = [
    {
      id: "length",
      label: `${PASSWORD_MIN}–${PASSWORD_MAX} characters`,
      ok: pw.length >= PASSWORD_MIN && pw.length <= PASSWORD_MAX,
    },
    {
      id: "charset",
      label: `No spaces or ${FORBIDDEN_CHARS.split("").join(" ")}`,
      ok: pw.length > 0 && [...pw].every(allowedChar),
    },
    { id: "classes", label: "3 of: lowercase, uppercase, digits, symbols", ok: characterClasses(pw) >= 3 },
    {
      id: "not_username",
      label: "Not the username",
      ok: pw.length > 0 && pw.toLowerCase() !== username.trim().toLowerCase(),
    },
  ];
  if (current) checks.push({ id: "changed", label: "Different from the current password", ok: pw !== current });
  return { ok: checks.every((c) => c.ok), checks };
}

/** Uniform random index in [0, n) from a byte source, rejecting biased bytes. */
function randomIndex(n: number, nextByte: () => number): number {
  const limit = 256 - (256 % n);
  for (;;) {
    const b = nextByte();
    if (b < limit) return b % n;
  }
}

/** Default byte source: the browser / Node Web Crypto CSPRNG. */
function cryptoByte(): number {
  const buf = new Uint8Array(1);
  globalThis.crypto.getRandomValues(buf);
  return buf[0] ?? 0; // always defined for a 1-byte buffer; satisfies noUncheckedIndexedAccess
}

/**
 * A strong password that always passes checkPassword (retries the rare draw missing 3 classes).
 * `nextByte` is injectable so the unit test is deterministic.
 */
export function generatePassword(length = 24, nextByte: () => number = cryptoByte): string {
  for (;;) {
    let out = "";
    for (let i = 0; i < length; i++) out += GENERATE_ALPHABET[randomIndex(GENERATE_ALPHABET.length, nextByte)];
    if (checkPassword(out, "").ok) return out;
  }
}

export type LoginState = "ok" | "failed" | "unreachable" | "unknown";
export type Tone = "healthy" | "warn" | "critical" | "neutral";

/** Operational wording + status tone for "can Meridian log into this switch?". */
export function loginBadge(state: LoginState, serviceInstalled: boolean): { tone: Tone; text: string } {
  switch (state) {
    case "ok":
      return { tone: "healthy", text: serviceInstalled ? "Meridian can log in" : "Meridian can log in (global login)" };
    case "failed":
      return { tone: "critical", text: "Needs bootstrap" };
    case "unreachable":
      return { tone: "warn", text: "CLI unreachable" };
    default:
      return { tone: "neutral", text: "Login not checked" };
  }
}

export interface AccessLike {
  mode: "direct" | "relay" | "unresolved" | null;
  switch_host: string | null;
  gateway_name: string | null;
}

/** "Direct · 10.1.1.2" / "Via JH-GW-01 · 192.168.1.32" / "No path". */
export function accessLabel(access: AccessLike | null | undefined): string {
  if (!access || access.mode === "unresolved" || access.mode === null) return "No path";
  const host = access.switch_host ? ` · ${access.switch_host}` : "";
  return access.mode === "relay" ? `Via ${access.gateway_name ?? "gateway"}${host}` : `Direct${host}`;
}

/** Plain words for the machine step names the apply/bootstrap engine returns. */
export function stepLabel(step: string): string {
  const words: Record<string, string> = {
    check_login: "Check Meridian login",
    set_admin_password: "Set new admin password (not saved)",
    verify_admin_login_and_save: "Log in with the new admin password, then save",
    revert_admin_password: "Put the previous admin password back",
    set_service_account: "Set Meridian's site service login (not saved)",
    verify_service_login_and_save: "Log in as Meridian with the new login, then save",
    revert_service_account: "Put Meridian's previous login back",
  };
  return words[step] ?? step;
}

/* ---------------------------------------------------------------------------------------------
 * Switch counts + deployment notice (OP#1856)
 * ------------------------------------------------------------------------------------------- */

/** Why the server left a switch off this card (`excluded_switches[].reason`). */
export type ExcludedReason = "no_cli";

/** The sentence for switches the card leaves out. SonicFi RSW: no ecCLI, so adopt preview owns it. */
export function excludedText(count: number): string {
  if (count <= 0) return "";
  return `${count} switch${count === 1 ? "" : "es"} not supported (no CLI) — managed via adopt preview`;
}

/**
 * The "Switches" sub-line. Before OP#1856 a site whose only switch was a SonicFi RSW read
 * "No switches at this site." while the VLANs tab counted 1 switch; the excluded switches are
 * now counted and named, so the card agrees with every other tab.
 */
export function switchesSummary(targets: number, pending: number, excluded: number): string {
  const ex = excludedText(excluded);
  if (targets === 0) return ex ? `${ex}.` : "No switches at this site.";
  const base = `${targets} switch${targets === 1 ? "" : "es"} · ${pending} to update`;
  return ex ? `${base} · ${ex}` : base;
}

/** The Apply button label. With no switch the card can apply to, it never says "Apply to 0 switches". */
export function applyLabel(targets: number, pending: number, running: boolean): string {
  if (running) return "Applying…";
  if (targets === 0) return "Nothing to apply";
  return `Apply to ${pending} switch${pending === 1 ? "" : "es"}`;
}

/** What a customer reads when the controller has no SITE_CREDENTIAL_KEY (mirrors the server). */
export const KEY_MISSING_PUBLIC = "Device credential storage isn't set up yet. Contact support.";

/**
 * The missing-key notice. The Docker-secret instructions are for platform_admin, the only role
 * that can fix the deployment (#1748); everyone else gets the neutral sentence. The server
 * applies the same rule to its own refusal text.
 */
export function keyMissingText(isPlatformAdmin: boolean): string {
  return isPlatformAdmin
    ? "The controller has no SITE_CREDENTIAL_KEY, so nothing can be stored or applied. Add the site_credential_key Docker secret."
    : KEY_MISSING_PUBLIC;
}
